/**
 * SidebarDrawer.tsx — Mobile off-canvas drawer (N-19).
 *
 * Renders the same grouped navigation as <Sidebar> but as an off-canvas
 * drawer that slides in from the left when the hamburger button is tapped.
 * Includes:
 *   - Backdrop overlay (semi-transparent, click to close)
 *   - ESC key to close
 *   - Focus trap (Tab cycles within drawer; Shift+Tab reverses)
 *   - Restore focus to the trigger button on close
 *   - prefers-reduced-motion respected (instant show/hide)
 *   - aria-modal + role="dialog" semantics
 */

import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import {
  getNavigationGrouped,
  isDestinationActive,
  type PortalContext,
  type ShellUser,
} from '../../lib/navigation.js';
import { NavIcon } from './NavIcon.js';
import { SidebarProfile } from './SidebarProfile.js';

export interface SidebarDrawerProps {
  context: PortalContext;
  pathname: string;
  hash?: string;
  user: ShellUser | null;
  open: boolean;
  onClose: () => void;
  /** ref to the trigger button so we can restore focus on close */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SidebarDrawer({
  context,
  pathname,
  hash = '',
  user,
  open,
  onClose,
  triggerRef,
}: SidebarDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement | null>(null);

  // Lock body scroll while the drawer is open so the page underneath doesn't
  // move when the user tries to scroll the (visually hidden) backdrop.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  // Move focus into the drawer when it opens; restore on close.
  useEffect(() => {
    if (open && drawerRef.current) {
      const first = drawerRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    } else if (!open && triggerRef?.current) {
      triggerRef.current.focus();
    }
  }, [open, triggerRef]);

  // ESC + focus trap
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusables = Array.from(
        drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (context === 'public') return null;
  if (!open) return null;

  const sections = getNavigationGrouped(context);

  return (
    <div className="sidebar-drawer-root" data-open={open ? 'true' : 'false'}>
      <button
        type="button"
        className="sidebar-drawer-backdrop"
        aria-label="Tutup menu"
        onClick={onClose}
        tabIndex={-1}
      />
      <div
        ref={drawerRef}
        className="sidebar-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menu utama"
        data-testid="sidebar-drawer"
      >
        <header className="sidebar-drawer-header">
          <span className="sidebar-drawer-title">Menu</span>
          <button
            type="button"
            className="sidebar-drawer-close"
            aria-label="Tutup menu"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <nav className="sidebar-drawer-nav" aria-label="Menu utama">
          {sections.map((section) => (
            <div key={section.label} className="sidebar-section">
              <h3 className="sidebar-section-label">{section.label}</h3>
              <ul className="sidebar-section-items" role="list">
                {section.items.map((destination) => {
                  const active = isDestinationActive(destination, pathname, hash);
                  if (destination.href) {
                    return (
                      <li key={destination.label}>
                        <a
                          href={
                            destination.hash
                              ? `${destination.href}#${destination.hash}`
                              : destination.href
                          }
                          className={`sidebar-link ${active ? 'is-active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                          onClick={onClose}
                          data-testid={`sidebar-drawer-link-${destination.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <span className="sidebar-link-icon">
                            <NavIcon icon={destination.icon} size={20} />
                          </span>
                          <span className="sidebar-link-label">{destination.label}</span>
                        </a>
                      </li>
                    );
                  }
                  return (
                    <li key={destination.label}>
                      <span
                        className="sidebar-link is-disabled"
                        aria-disabled="true"
                        title={destination.disabledReason ?? 'Segera tersedia'}
                      >
                        <span className="sidebar-link-icon">
                          <NavIcon icon={destination.icon} size={20} />
                        </span>
                        <span className="sidebar-link-label">{destination.label}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {user && (
            <div className="sidebar-drawer-user">
              <SidebarProfile user={user} context={context} />
            </div>
          )}
        </nav>
      </div>
    </div>
  );
}
